import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Heart, Sparkles, Printer, Save, RefreshCw, FileText, Pencil, LogOut, MapPin, Clock, CalendarCheck, Mail, Loader2, Bell } from "lucide-react";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useT } from "../lib/i18n";
import { useAuth } from "../lib/auth";
import { fmtDate, fmtDateTime } from "../lib/options";
import { Empty, Spinner, Chip, PageTitle } from "../components/Bits";
import { JobCard } from "../components/JobCard";
import { useMarks } from "./Jobs";

export function MyApplications() {
  const { t, lang } = useT();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/candidate/applications").then((r) => setD(r.data)).catch(() => setD({ applications: [], invitations: [] })); }, []);
  if (!d) return <Spinner label={t("loading")} />;
  return (
    <div className="narrow pb-10">
      <PageTitle>{t("apps_title")}</PageTitle>
      {d.invitations.length > 0 && (
        <div className="mb-6 space-y-3" data-testid="invitations-list">
          <h2 className="text-lg font-bold text-[color:var(--navy)]">{t("invites")}</h2>
          {d.invitations.map((i) => (
            <div key={i.id} className="card-soft flex items-center gap-3 border-[color:var(--orange)] bg-[color:var(--orange-soft)] p-4" data-testid={`invitation-${i.id}`}>
              <Mail className="shrink-0 text-[color:var(--orange)]" />
              <div className="flex-1"><p className="font-bold">{i.organizations?.name} {t("invite_from")}</p>{i.jobs && <p className="text-sm text-slate-600">{i.jobs.title}</p>}</div>
              {i.jobs && <Link to={`/jobs/${i.jobs.id}`} className="rounded-xl bg-[color:var(--orange)] px-4 py-3 font-bold text-white" data-testid={`invitation-open-${i.id}`}>{t("view")}</Link>}
            </div>
          ))}
        </div>
      )}
      {d.applications.length === 0 ? (
        <Empty icon={ClipboardList} title={t("no_apps_t")} sub={t("no_apps_sub")} testid="apps-empty-state"><Link to="/jobs" className="btn-orange" data-testid="apps-empty-find-btn">{t("find_job")}</Link></Empty>
      ) : (
        <div className="space-y-3" data-testid="my-applications-list">
          {d.applications.map((a) => (
            <Link to={a.jobs ? `/jobs/${a.jobs.id}` : "#"} key={a.id} className="card-soft block p-4" data-testid={`my-application-${a.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div><p className="text-lg font-bold">{a.jobs?.title}</p><p className="text-slate-600">{a.jobs?.organizations?.name} · {a.jobs?.location}</p></div>
                <Chip status={a.status} label={t(`st_${a.status}`)} />
              </div>
              {a.interview_at && <p className="mt-2 flex items-center gap-2 font-semibold text-amber-800"><CalendarCheck size={18} />{t("interview_on")} {fmtDateTime(a.interview_at, lang)} {a.interview_place && `· ${a.interview_place}`}</p>}
              <p className="mt-2 text-sm text-slate-500">{fmtDate(a.created_at, lang)}</p>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}

export function MySaved() {
  const { t } = useT();
  const [jobs, setJobs] = useState(null);
  const { marks, toggleSave } = useMarks();
  useEffect(() => { api.get("/candidate/saved").then((r) => setJobs(r.data)).catch(() => setJobs([])); }, []);
  if (!jobs) return <Spinner label={t("loading")} />;
  const visible = jobs.filter((j) => marks.saved.includes(j.id));
  return (
    <div className="page pb-10">
      <PageTitle>{t("saved_title")}</PageTitle>
      {visible.length === 0 ? (
        <Empty icon={Heart} title={t("no_saved_t")} sub={t("no_saved_sub")} testid="saved-empty-state"><Link to="/jobs" className="btn-orange" data-testid="saved-empty-find-btn">{t("find_job")}</Link></Empty>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">{visible.map((j) => <JobCard key={j.id} job={j} saved onToggleSave={toggleSave} />)}</div>
      )}
    </div>
  );
}

const Row = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 py-3"><Icon size={20} className="mt-0.5 shrink-0 text-[color:var(--orange)]" /><div><p className="text-sm text-slate-500">{label}</p><p className="font-semibold">{value || "—"}</p></div></div>
);

export function MyProfile() {
  const { t, lang } = useT();
  const { user, logout } = useAuth();
  const [p, setP] = useState(null);
  useEffect(() => { api.get("/candidate/profile").then((r) => setP(r.data)).catch(() => setP({})); }, []);
  if (!p) return <Spinner label={t("loading")} />;
  const openCv = async () => {
    try { const { data } = await api.get("/candidate/cv"); window.open(data.url, "_blank", "noopener"); } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleAlerts = async (v) => {
    setP((x) => ({ ...x, job_alerts: v }));
    try { await api.put("/candidate/profile", { job_alerts: v, lang }); toast.success(t("saved_ok")); } catch (e) { setP((x) => ({ ...x, job_alerts: !v })); toast.error(errMsg(e)); }
  };
  return (
    <div className="narrow pb-10">
      <div className="mt-6 flex items-center gap-4">
        {user.picture && <img src={user.picture} alt="" className="h-16 w-16 rounded-full" />}
        <div><h1 className="text-2xl font-bold text-[color:var(--navy)]" data-testid="profile-name">{p.full_name || user.name}</h1><p className="text-slate-600">{user.email}</p></div>
      </div>
      {!p.completed && (
        <div className="mt-6 rounded-2xl bg-amber-50 p-4" data-testid="profile-incomplete-banner">
          <p className="font-bold text-amber-900">{t("profile_incomplete")}</p>
          <Link to="/start" className="btn-orange mt-3 !w-full" data-testid="profile-complete-btn">{t("complete_now")}</Link>
        </div>
      )}
      <div className="card-soft mt-6 divide-y px-5">
        <Row icon={MapPin} label={t("lives")} value={p.location} />
        <Row icon={ClipboardList} label={t("wants")} value={(p.job_types || []).map((v) => t(`cat_${v}`)).join(", ")} />
        <Row icon={Sparkles} label={t("experience_l")} value={p.experience && t(`e_${p.experience}`)} />
        <Row icon={Clock} label={t("hours_l")} value={(p.hours || []).map((v) => t(`h_${v}`)).join(", ")} />
        <Row icon={CalendarCheck} label={t("can_start")} value={p.availability && t(`a_${p.availability}`)} />
        <div className="py-3 font-semibold" data-testid="profile-visibility">{p.visible_to_employers ? t("visible_yes") : t("visible_no")}</div>
        <label className="flex min-h-[64px] items-center justify-between gap-3 py-3">
          <span className="flex items-center gap-3"><Bell size={20} className="shrink-0 text-[color:var(--orange)]" /><span><span className="block font-semibold">{t("alerts_label")}</span><span className="block text-sm text-slate-500">{t("alerts_sub")}</span></span></span>
          <Switch checked={!!p.job_alerts} onCheckedChange={toggleAlerts} data-testid="profile-job-alerts-switch" />
        </label>
      </div>
      <div className="mt-6 grid gap-3">
        <Link to="/start" className="btn-ghost !w-full" data-testid="profile-edit-btn"><Pencil size={20} />{t("edit_profile")}</Link>
        <Link to="/me/cv" className="btn-navy !w-full" data-testid="profile-cv-btn"><Sparkles size={20} />{t("ai_cv_btn")}</Link>
        {p.cv_path && <button className="btn-ghost !w-full" onClick={openCv} data-testid="profile-view-uploaded-cv-btn"><FileText size={20} />{t("uploaded_cv")} · {t("view")}</button>}
        <button className="btn-ghost !w-full !text-red-600" onClick={logout} data-testid="profile-logout-btn"><LogOut size={20} />{t("sign_out")}</button>
      </div>
    </div>
  );
}

export function MyCV() {
  const { t, lang } = useT();
  const [text, setText] = useState(null);
  const [busy, setBusy] = useState(false);
  const [cvLang, setCvLang] = useState(lang);
  const [completed, setCompleted] = useState(true);
  useEffect(() => { api.get("/candidate/profile").then((r) => { setText(r.data.ai_cv || ""); setCompleted(!!r.data.completed); }).catch(() => setText("")); }, []);
  const gen = async () => {
    setBusy(true);
    try { const { data } = await api.post("/candidate/ai-cv", { lang: cvLang }, { timeout: 120000 }); setText(data.ai_cv); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const save = async () => {
    try { await api.put("/candidate/ai-cv", { ai_cv: text }); toast.success(t("saved_ok")); } catch (e) { toast.error(errMsg(e)); }
  };
  if (text === null) return <Spinner />;
  if (!completed) return <div className="narrow py-10"><Empty icon={FileText} title={t("profile_incomplete")} testid="cv-incomplete"><Link to="/start" className="btn-orange" data-testid="cv-complete-btn">{t("complete_now")}</Link></Empty></div>;
  return (
    <div className="narrow pb-10">
      <PageTitle sub={t("ai_cv_sub")}>{t("ai_cv_t")}</PageTitle>
      <div className="mb-4 flex items-center gap-3">
        <span className="font-semibold">{t("ai_lang")}</span>
        {["fr", "en"].map((l) => <button key={l} onClick={() => setCvLang(l)} className={`min-h-[44px] rounded-full px-4 font-bold uppercase ${cvLang === l ? "bg-[color:var(--navy)] text-white" : "bg-slate-100"}`} data-testid={`cv-lang-${l}`}>{l}</button>)}
      </div>
      {busy ? (
        <div className="card-soft flex flex-col items-center gap-3 p-10 text-center" data-testid="cv-generating"><Loader2 className="animate-spin text-[color:var(--orange)]" size={40} /><p className="font-semibold">{t("ai_writing")}</p></div>
      ) : text ? (
        <>
          <textarea id="cv-print" value={text} onChange={(e) => setText(e.target.value)} rows={20} className="field !py-4 font-mono !text-sm leading-relaxed" data-testid="cv-text" />
          <div className="mt-4 grid gap-3 sm:grid-cols-3">
            <button className="btn-orange" onClick={save} data-testid="cv-save-btn"><Save size={20} />{t("ai_save")}</button>
            <button className="btn-navy" onClick={() => window.print()} data-testid="cv-print-btn"><Printer size={20} />{t("ai_print")}</button>
            <button className="btn-ghost" onClick={gen} data-testid="cv-regenerate-btn"><RefreshCw size={20} />{t("ai_regen")}</button>
          </div>
        </>
      ) : (
        <button className="btn-orange !w-full !min-h-[64px]" onClick={gen} data-testid="cv-generate-btn"><Sparkles size={22} />{t("ai_cv_btn")}</button>
      )}
    </div>
  );
}
